/**
 * Complexity Indicator View — 2-tab layout: Assessment + Feed.
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Button } from "@/ui/primitives/button";
import { Badge } from "@/ui/primitives/badge";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/ui/composed/sections/tabs";
import { AiDisclaimer } from "@/ui/composed/ai-disclaimer";
import {
  ArrowLeft,
  RotateCcw,
  Gauge,
  Bot,
  Loader2,
  MessageSquare,
} from "lucide-react";
import { formatDuration } from "../shared";
import { StatusDot } from "../StatusBadges";
import { getTaskTree, UnifiedFeed } from "../shared-panels";
import { SummaryChatSheet } from "../SummaryChatSheet";
import { ExportButton } from "@/ui/composed/ExportButton";
import { DocumentDetail } from "../../documents/DocumentDetail";
import { SignalBanner, SignalDot } from "./SignalBanner";
import { CriterionCard } from "./CriterionCard";
import {
  criteriaForCaseType,
  type IndicatorViewProps,
  type IndicatorOutput,
} from "./types";
import {
  buildLiveCriteria,
  buildLiveSecuredInterestsFlag,
  groupFindingsByKey,
  collectSourceDocs,
} from "./helpers";
import { isPersonalInsolvencyAppointmentType } from "@cvx/lib/appointmentTypes";

export function IndicatorView({
  taskId,
  jobId,
  onBack,
  onRerun,
  isViewer,
}: IndicatorViewProps) {
  const [viewingDocumentId, setViewingDocumentId] =
    useState<Id<"documents"> | null>(null);
  const [chatOpen, setChatOpen] = useState(false);

  const { data: task } = useQuery(
    convexQuery(
      api.jobs.summarization.publicQueries.getSummarizationTaskDetail,
      { taskId },
    ),
  );
  const { data: findings } = useQuery(
    convexQuery(
      api.jobs.summarization.publicQueries.listIndicatorFindingsForJob,
      { jobId },
    ),
  );
  const { data: job } = useQuery(
    convexQuery(api.jobs.queries.getJob, { jobId }),
  );
  const isPersonal = isPersonalInsolvencyAppointmentType(job?.appointmentType);

  if (!task) return null;

  const isRunning = task.status === "running";
  const isCompleted = task.status === "completed";
  const isFailed = task.status === "failed";
  const isWaiting = task.status === "waiting";

  const output = task.output as IndicatorOutput | undefined;
  const signal = output?.signal ?? (isRunning ? undefined : "green");
  const criteriaMet = output?.criteriaMet ?? 0;

  const liveCriteria = buildLiveCriteria(findings ?? []);
  const criteria =
    output?.criteria ??
    (isRunning || findings?.length ? liveCriteria : undefined);
  const findingsByKey = groupFindingsByKey(findings ?? []);

  const securedInterestsFlag =
    output?.securedInterestsFlag ??
    buildLiveSecuredInterestsFlag(findings ?? []);

  return (
    <Tabs
      defaultValue={isRunning ? "feed" : "indicator"}
      className="-m-6 flex flex-1 flex-col bg-muted px-6 pb-6 [overflow-anchor:none]"
    >
      {/* Sticky header + tab bar — pins below the dashboard header */}
      <div className="sticky top-16 z-[9] -mx-6 bg-muted px-6 pb-2 pt-6">
        <div className="flex items-center gap-3 pb-4">
          <Button
            variant="outline"
            size="sm"
            onClick={onBack}
            className="gap-1.5"
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </Button>
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <StatusDot status={task.status} />
            <h3 className="text-lg font-semibold">Complexity Indicator</h3>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {task.duration != null && (
              <Badge variant="outline" className="text-xs tabular-nums">
                {formatDuration(task.duration)}
              </Badge>
            )}
            {isCompleted && task.output && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setChatOpen(true)}
                className="gap-1.5"
              >
                <MessageSquare className="h-3.5 w-3.5" /> Chat
              </Button>
            )}
            {isCompleted && task.output && (
              <ExportButton
                content={{
                  title: "Complexity Indicator",
                  output: task.output as Record<string, unknown>,
                }}
                filename="Complexity_Indicator"
                formats={["word", "pdf"]}
              />
            )}
            {(isRunning || isCompleted || isFailed) && (
              <Button
                variant="outline"
                size="sm"
                onClick={onRerun}
                disabled={isViewer}
                className="gap-1.5"
              >
                <RotateCcw className="h-3.5 w-3.5" /> Re-run
              </Button>
            )}
          </div>
        </div>

        {!isWaiting && (
          <TabsList className="w-full">
            <TabsTrigger value="indicator" className="gap-1.5">
              <Gauge className="h-3.5 w-3.5" />
              Assessment
              {isCompleted && signal && <SignalDot signal={signal} />}
            </TabsTrigger>
            <TabsTrigger value="feed" className="gap-1.5">
              <Bot className="h-3.5 w-3.5" />
              Agent
              {isRunning && (
                <span className="relative ml-1 flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                </span>
              )}
            </TabsTrigger>
          </TabsList>
        )}
      </div>

      {/* Waiting */}
      {isWaiting && (
        <div className="flex flex-col items-center gap-3 py-16">
          <div className="rounded-full bg-muted/50 p-4">
            <Gauge className="h-8 w-8 text-muted-foreground/40" />
          </div>
          <div className="text-center">
            <p className="text-sm font-medium">Ready to assess</p>
            <p className="mt-1 text-xs text-muted-foreground">
              {isPersonal
                ? "Evaluates matter complexity based on the bankrupt's assets, creditor exposure, and secured interests."
                : "Evaluates job complexity based on company assets, creditor exposure, director assets, and secured interests."}
            </p>
          </div>
        </div>
      )}

      {/* Tab content */}
      {!isWaiting && (
        <>
          <TabsContent value="indicator" className="mt-2">
            <div className="flex flex-col gap-6 pb-8 pr-4">
              <SignalBanner
                signal={signal}
                criteriaMet={criteriaMet}
                recommendation={output?.recommendation}
                isRunning={isRunning}
              />
              {criteria && (
                <div className="space-y-3">
                  <h4 className="font-sans text-xs font-semibold uppercase tracking-wider text-[#121212]">
                    Assessment Criteria
                  </h4>
                  <div className="space-y-3">
                    {criteriaForCaseType(isPersonal).map((cfg) => (
                      <CriterionCard
                        key={cfg.key}
                        config={cfg}
                        criterion={criteria[cfg.key]}
                        sourceDocuments={collectSourceDocs(
                          findingsByKey[cfg.dbKey],
                        )}
                        onViewDocument={(docId) =>
                          setViewingDocumentId(docId as Id<"documents">)
                        }
                        isRunning={isRunning}
                        securedInterestsFlag={
                          cfg.key === "creditorThreshold"
                            ? securedInterestsFlag
                            : undefined
                        }
                      />
                    ))}
                  </div>
                </div>
              )}
              {isRunning && findings && findings.length > 0 && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  {findings.length} finding{findings.length === 1 ? "" : "s"}{" "}
                  extracted — consolidating...
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="feed" className="mt-2">
            <UnifiedFeed
              task={task}
              subAgents={[]}
              taskTree={getTaskTree(task.workingMemory)}
              isRunning={isRunning}
            />
          </TabsContent>
        </>
      )}

      <DocumentDetail
        documentId={viewingDocumentId}
        isViewer
        open={viewingDocumentId !== null}
        onClose={() => setViewingDocumentId(null)}
      />
      <SummaryChatSheet
        taskId={taskId}
        jobId={jobId}
        agentName="Complexity Indicator"
        open={chatOpen}
        onOpenChange={setChatOpen}
      />
      <AiDisclaimer className="mt-4 text-center" />
    </Tabs>
  );
}
